import { useState, useEffect, useRef } from 'react';
import './header.css';
import UserMenu from '../UserMenu';
import Icon from '../Icon';
import menuIcon from '../../imgs/menu.png';
import bertoliToursLogo from '../../imgs/bertolitours.png';
import whiteBertoliToursLogo from '../../imgs/white-bertolitours.png';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../../UserContext';
import { useTheme } from '../../ThemeContext';
import { THEMES } from '../../constants/theme';
import { getBlob } from '../../services/api';

let cachedUserPhoto = {
    key: undefined,
    url: undefined,
};

function clearCachedUserPhoto() {
    if (cachedUserPhoto.url) {
        URL.revokeObjectURL(cachedUserPhoto.url);
    }

    cachedUserPhoto = {
        key: undefined,
        url: undefined,
    };
}

function Header() {
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showNavMenu, setShowNavMenu] = useState(false);
    const [userPhotoUrl, setUserPhotoUrl] = useState(undefined);
    const buttonMenuRef = useRef(null);
    const menuRef = useRef(null);
    const [pos, setPos] = useState(null);
    const { user } = useUser();
    const { resolvedTheme } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const logoImage = resolvedTheme === THEMES.DARK ? whiteBertoliToursLogo : bertoliToursLogo;
    const userInitials = `${user?.name?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase() || '??';

    function isMenuItemActive(menuKey) {
        const { pathname } = location;

        if (menuKey === 'modules') {
            return pathname === '/modules' || pathname.startsWith('/module/');
        }

        if (menuKey === 'news') {
            return pathname === '/news' || pathname.startsWith('/news/');
        }

        if (menuKey === 'materials') {
            return pathname === '/materials' || pathname.startsWith('/material/');
        }

        if (menuKey === 'community') {
            return pathname === '/community';
        }

        return false;
    }

    useEffect(() => {
        const photoCacheKey = !!user?.photoPath
            ? `${user.photoPath}:${user?.photoVersion ?? 0}`
            : undefined;

        if (!photoCacheKey) {
            clearCachedUserPhoto();
            setUserPhotoUrl(undefined);
            return;
        }

        if (cachedUserPhoto.key === photoCacheKey && !!cachedUserPhoto.url) {
            setUserPhotoUrl(cachedUserPhoto.url);
            return;
        }

        let isMounted = true;

        getBlob('/user/photo', true, () => {},
            (response) => {
                if (!isMounted) {
                    return;
                }

                const objectUrl = URL.createObjectURL(response.data);

                clearCachedUserPhoto();
                cachedUserPhoto = {
                    key: photoCacheKey,
                    url: objectUrl,
                };

                setUserPhotoUrl(objectUrl);
            },
            () => {
                if (!isMounted) {
                    return;
                }

                setUserPhotoUrl(undefined);
            },
            () => {},
            'none',
            'none'
        );

        return () => {
            isMounted = false;
        };
    }, [user?.photoPath, user?.photoVersion]);

    useEffect(() => {
        function handleClickOutside(e) {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setShowUserMenu(false);
                setPos(null);
            }
        }

        if (showUserMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            const rect = buttonMenuRef.current.getBoundingClientRect();
            setPos({
                top: rect.bottom + 8,
                right: window.innerWidth - rect.right,
            });
        } else {
            setPos(null);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showUserMenu]);

    useEffect(() => {
        function handleResize() {
            if (window.innerWidth >= 800 && showNavMenu) {
                setShowNavMenu(false);
            }
        }

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [showNavMenu]);

    return (
        <>
            <header id='header' ref={menuRef}>
                <button
                    id="nav-toggle"
                    aria-label="Abrir menu"
                    aria-expanded={showNavMenu}
                    onClick={() => setShowNavMenu((prev) => !prev)}>
                    <img src={menuIcon} alt="chave" />
                </button>

                <div id='logo' onClick={() => navigate('/')}>
                    <img id='logo-image' src={logoImage} alt='Bertoli HUB' />
                </div>

                <div id="menu-wrapper">
                    <ul id="menu">
                        <li
                            className={isMenuItemActive('modules') ? 'menu-active' : ''}
                            onClick={() => navigate('/modules')}>
                            <span className='menu-item-content'>
                                <Icon iconClass='icon-menu-classes' size={17} />
                                <span>Aulas</span>
                            </span>
                        </li>
                        <li
                            className={isMenuItemActive('news') ? 'menu-active' : ''}
                            onClick={() => navigate('/news')}>
                            <span className='menu-item-content'>
                                <Icon iconClass='icon-menu-news' size={17} />
                                <span>Notícias</span>
                            </span>
                        </li>
                        <li
                            className={isMenuItemActive('materials') ? 'menu-active' : ''}
                            onClick={() => navigate('/materials')}>
                            <span className='menu-item-content'>
                                <Icon iconClass='icon-menu-materials' size={17} />
                                <span>Materiais</span>
                            </span>
                        </li>
                        <li
                            className={isMenuItemActive('community') ? 'menu-active' : ''}
                            onClick={() => navigate('/community')}>
                            <span className='menu-item-content'>
                                <Icon iconClass='icon-menu-community' size={17} />
                                <span>Comunidade</span>
                            </span>
                        </li>
                    </ul>
                </div>

                <div id="configs" ref={buttonMenuRef}>
                    <button
                        id="configs-button"
                        onClick={() => {
                            setShowUserMenu((prev) => {
                                if (prev) {
                                    setPos(null);
                                    return false;
                                }

                                const rect = buttonMenuRef.current?.getBoundingClientRect();

                                if (rect) {
                                    setPos({
                                        top: rect.bottom + 8,
                                        right: window.innerWidth - rect.right,
                                    });
                                }

                                return true;
                            });
                        }}>
                        <span className='background-button zoom-effect'></span>
                        {!!userPhotoUrl && (
                            <img
                                id="user-photo"
                                src={userPhotoUrl}
                                alt={`Foto de ${user?.name ?? 'utilizador'}`}
                            />
                        )}
                        {!userPhotoUrl && <span id="user-initials">{userInitials}</span>}
                    </button>
                </div>
                
                <UserMenu
                    user={user}
                    pos={pos}
                    showMenu={showUserMenu && !!pos}
                    onPerfilClick={() => setShowUserMenu(false)}
                    onChangePassClick={() => setShowUserMenu(false)} />
            </header>

            {showNavMenu && (
                <button
                    className="side-menu-overlay"
                    aria-label="Fechar menu"
                    onClick={() => setShowNavMenu(false)}
                />
            )}

            <nav className={`side-menu glass-effect-75 ${showNavMenu ? 'open' : ''}`} aria-hidden={!showNavMenu}>
                <ul className="side-menu-list">
                    <li
                        className={`side-menu-item ${isMenuItemActive('modules') ? 'menu-active' : ''}`}
                        onClick={() => {
                            setShowNavMenu(false);
                            navigate('/modules');
                        }}>
                        <span className='menu-item-content'>
                            <Icon iconClass='icon-menu-classes' size={17} />
                            <span>Aulas</span>
                        </span>
                    </li>
                    <li
                        className={`side-menu-item ${isMenuItemActive('news') ? 'menu-active' : ''}`}
                        onClick={() => {
                            setShowNavMenu(false);
                            navigate('/news');
                        }}>
                        <span className='menu-item-content'>
                            <Icon iconClass='icon-menu-news' size={17} />
                            <span>Notícias</span>
                        </span>
                    </li>
                    <li
                        className={`side-menu-item ${isMenuItemActive('materials') ? 'menu-active' : ''}`}
                        onClick={() => {
                            setShowNavMenu(false);
                            navigate('/materials');
                        }}>
                        <span className='menu-item-content'>
                            <Icon iconClass='icon-menu-materials' size={17} />
                            <span>Materiais</span>
                        </span>
                    </li>
                    <li
                        className={`side-menu-item ${isMenuItemActive('community') ? 'menu-active' : ''}`}
                        onClick={() => {
                            setShowNavMenu(false);
                            navigate('/community');
                        }}>
                        <span className='menu-item-content'>
                            <Icon iconClass='icon-menu-community' size={17} />
                            <span>Comunidade</span>
                        </span>
                    </li>
                </ul>
            </nav>
        </>
    )
}

export default Header;